import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  BreadcrumbGroup,
  Button,
  CopyToClipboard,
  Flashbar,
  FlashbarProps,
  Header,
  Input,
  Modal,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
} from "@cloudscape-design/components";
import { getAwsCreds } from "./awsCredentials";
import { apiFetch } from "./apiFetch";

const DEFAULT_ACCOUNT_ID = "649150965830"; // "Application" account
const DEFAULT_REGION = "eu-west-1";

interface Account {
  documentId: string;
  Alias: string;
  accountid: string;
}

interface InferenceProfile {
  arn: string;
  inferenceProfileId: string;
  inferenceProfileName: string;
  description?: string;
  vendor: string;
  modelName?: string;
  foundationModelId?: string;
  geoScope?: string;
  region: string;
  destinationRegions: string[];
  modelArns: string[];
  status?: string;
  awsCreatedAt?: string;
  awsUpdatedAt?: string;
}

interface StaleProfile {
  documentId: string;
  arn: string;
  inferenceProfileName: string;
  vendor: string;
  modelName?: string;
}

interface SyncPreview {
  toCreate: number;
  toUpdate: number;
  toDelete: StaleProfile[];
  total: number;
  region: string;
}

interface SyncResult {
  created: number;
  updated: number;
  deleted: number;
  keptStale: number;
  total: number;
  region: string;
}

type SortField = keyof InferenceProfile;

function statusType(s?: string): "success" | "error" | "warning" | "info" {
  if (!s) return "info";
  if (/active/i.test(s)) return "success";
  if (/failed|error/i.test(s)) return "error";
  if (/pending|creating|updating/i.test(s)) return "warning";
  return "info";
}

export default function LiveFmSdipPage() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [region, setRegion] = useState(DEFAULT_REGION);
  const [vendorFilter, setVendorFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");

  const [profiles, setProfiles] = useState<InferenceProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [credsMissing, setCredsMissing] = useState(false);

  const [syncing, setSyncing] = useState(false);
  const [staleModal, setStaleModal] = useState<{ visible: boolean; stale: StaleProfile[] }>({
    visible: false,
    stale: [],
  });

  const [flash, setFlash] = useState<FlashbarProps.MessageDefinition[]>([]);
  const dismissFlash = () => setFlash([]);

  const [sortingColumn, setSortingColumn] = useState<{ sortingField?: string }>({ sortingField: "vendor" });
  const [sortingDescending, setSortingDescending] = useState(false);

  // Load accounts and pick the default (Application) account.
  useEffect(() => {
    (async () => {
      const res = await apiFetch(`/accounts`);
      const { data } = await res.json();
      setAccounts(data);
      if (data.length > 0) {
        const preferred =
          data.find((a: Account) => a.accountid === DEFAULT_ACCOUNT_ID) ?? data[0];
        setSelectedAccount(preferred);
      }
    })();
  }, []);

  const creds = useMemo(
    () => (selectedAccount ? getAwsCreds(selectedAccount.Alias) : null),
    [selectedAccount],
  );

  const authHeaders = useCallback(() => {
    if (!creds) return null;
    return {
      "X-AWS-Access-Key-Id": creds.accessKeyId,
      "X-AWS-Secret-Access-Key": creds.secretAccessKey,
      "X-AWS-Session-Token": creds.sessionToken,
    };
  }, [creds]);

  const fetchProfiles = useCallback(async () => {
    if (!selectedAccount) return;
    const headers = authHeaders();
    if (!headers) {
      setCredsMissing(true);
      setProfiles([]);
      return;
    }
    setCredsMissing(false);
    setLoading(true);
    try {
      const res = await apiFetch(
        `/inventory/live/fm-sdips?accountId=${selectedAccount.documentId}&region=${region}`,
        { headers },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProfiles(data.profiles ?? []);
    } catch (e: any) {
      setProfiles([]);
      setFlash([
        { type: "error", content: `Failed to load profiles: ${e.message}`, dismissible: true, onDismiss: dismissFlash },
      ]);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, region, authHeaders]);

  useEffect(() => {
    fetchProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount, region]);

  const runSync = useCallback(
    async (deleteArns: string[]) => {
      if (!selectedAccount) return;
      const headers = authHeaders();
      if (!headers) return;
      setSyncing(true);
      try {
        const res = await apiFetch(
          `/inventory/live/fm-sdips/sync?accountId=${selectedAccount.documentId}&region=${region}`,
          {
            method: "POST",
            headers: { ...headers, "Content-Type": "application/json" },
            body: JSON.stringify({ deleteArns }),
          },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const result: SyncResult = await res.json();
        let content = `Synced ${result.total} profiles (${result.created} created, ${result.updated} updated, ${result.deleted} deleted).`;
        if (result.keptStale > 0) {
          content += ` ${result.keptStale} profiles that no longer exist in AWS were kept in ITPMNG (not deleted).`;
        }
        setFlash([{ type: "success", content, dismissible: true, onDismiss: dismissFlash }]);
        await fetchProfiles();
      } catch (e: any) {
        setFlash([
          { type: "error", content: `Sync failed: ${e.message}`, dismissible: true, onDismiss: dismissFlash },
        ]);
      } finally {
        setSyncing(false);
      }
    },
    [selectedAccount, region, authHeaders, fetchProfiles],
  );

  const handleSyncClick = useCallback(async () => {
    if (!selectedAccount) return;
    const headers = authHeaders();
    if (!headers) return;
    setSyncing(true);
    try {
      const res = await apiFetch(
        `/inventory/live/fm-sdips/sync/preview?accountId=${selectedAccount.documentId}&region=${region}`,
        { method: "POST", headers: { ...headers, "Content-Type": "application/json" } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const preview: SyncPreview = await res.json();
      if (preview.toDelete.length === 0) {
        await runSync([]);
      } else {
        setStaleModal({ visible: true, stale: preview.toDelete });
      }
    } catch (e: any) {
      setFlash([
        { type: "error", content: `Sync preview failed: ${e.message}`, dismissible: true, onDismiss: dismissFlash },
      ]);
    } finally {
      setSyncing(false);
    }
  }, [selectedAccount, region, authHeaders, runSync]);

  // Filter + sort.
  const visibleProfiles = useMemo(() => {
    const filtered = profiles.filter((p) => {
      const vendorOk = !vendorFilter || p.vendor.toLowerCase().includes(vendorFilter.toLowerCase());
      const name = nameFilter.toLowerCase();
      const nameOk =
        !nameFilter ||
        p.inferenceProfileName.toLowerCase().includes(name) ||
        (p.modelName ?? "").toLowerCase().includes(name);
      return vendorOk && nameOk;
    });

    const field = (sortingColumn.sortingField as SortField) ?? "vendor";
    const sorted = [...filtered].sort((a, b) => {
      // Default: vendor, then modelName.
      if (field === "vendor") {
        const v = (a.vendor ?? "").localeCompare(b.vendor ?? "");
        if (v !== 0) return v;
        return (a.modelName ?? "").localeCompare(b.modelName ?? "");
      }
      const av = String(a[field] ?? "");
      const bv = String(b[field] ?? "");
      return av.localeCompare(bv);
    });
    return sortingDescending ? sorted.reverse() : sorted;
  }, [profiles, vendorFilter, nameFilter, sortingColumn, sortingDescending]);

  const accountOptions = accounts.map((a) => ({
    label: `${a.Alias} (${a.accountid})`,
    value: a.documentId,
  }));

  const destinationRegionsCell = (p: InferenceProfile) => {
    if (p.destinationRegions && p.destinationRegions.length > 0) {
      return p.destinationRegions.join(", ");
    }
    if ((p.geoScope ?? "").toLowerCase() === "global") return "All commercial regions";
    return "—";
  };

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup
        items={[
          { text: "Live Inventory", href: "/live-inventory" },
          { text: "FM SDIPs", href: "/live-inventory/fm-sdips" },
        ]}
      />

      <Flashbar items={flash} />

      <div className="filter-bar">
        <Select
          selectedOption={
            selectedAccount
              ? { label: `${selectedAccount.Alias} (${selectedAccount.accountid})`, value: selectedAccount.documentId }
              : null
          }
          onChange={({ detail }) => {
            const acc = accounts.find((a) => a.documentId === detail.selectedOption.value) ?? null;
            setSelectedAccount(acc);
          }}
          options={accountOptions}
          placeholder="Select account"
          filteringType="auto"
        />
        <Input value={region} onChange={({ detail }) => setRegion(detail.value)} placeholder="Region" />
        <Input value={vendorFilter} onChange={({ detail }) => setVendorFilter(detail.value)} placeholder="Vendor" />
        <Input value={nameFilter} onChange={({ detail }) => setNameFilter(detail.value)} placeholder="Name" />
      </div>

      {credsMissing && (
        <StatusIndicator type="warning">
          AWS credentials are missing for {selectedAccount?.Alias}. Configure them in Settings.
        </StatusIndicator>
      )}

      <Table
        header={
          <Header
            counter={`(${visibleProfiles.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="refresh" loading={loading} onClick={fetchProfiles} />
                <Button
                  variant="primary"
                  loading={syncing}
                  disabled={loading || syncing || credsMissing || !creds}
                  onClick={handleSyncClick}
                >
                  Sync FM SDIPs
                </Button>
              </SpaceBetween>
            }
          >
            FM System Defined Inference Profiles
          </Header>
        }
        items={visibleProfiles}
        loading={loading}
        sortingColumn={sortingColumn}
        sortingDescending={sortingDescending}
        onSortingChange={({ detail }) => {
          setSortingColumn(detail.sortingColumn);
          setSortingDescending(detail.isDescending ?? false);
        }}
        columnDefinitions={[
          {
            id: "inferenceProfileName",
            header: "Profile name",
            cell: (p: InferenceProfile) => p.inferenceProfileName,
            sortingField: "inferenceProfileName",
          },
          { id: "vendor", header: "Vendor", cell: (p: InferenceProfile) => p.vendor, sortingField: "vendor" },
          { id: "modelName", header: "Model name", cell: (p: InferenceProfile) => p.modelName ?? "—", sortingField: "modelName" },
          {
            id: "geoScope",
            header: "Geo scope",
            cell: (p: InferenceProfile) => (p.geoScope ? p.geoScope.toUpperCase() : "—"),
            sortingField: "geoScope",
          },
          {
            id: "inferenceProfileId",
            header: "Inference profile ID",
            cell: (p: InferenceProfile) => (
              <SpaceBetween direction="horizontal" size="xs">
                <span>{p.inferenceProfileId}</span>
                <CopyToClipboard
                  variant="icon"
                  textToCopy={p.inferenceProfileId}
                  copySuccessText="Copied"
                  copyErrorText="Failed to copy"
                />
              </SpaceBetween>
            ),
            sortingField: "inferenceProfileId",
          },
          {
            id: "status",
            header: "Status",
            cell: (p: InferenceProfile) => (
              <StatusIndicator type={statusType(p.status)}>{p.status ?? "—"}</StatusIndicator>
            ),
            sortingField: "status",
          },
          {
            id: "destinationRegions",
            header: "Destination regions",
            cell: destinationRegionsCell,
          },
          {
            id: "awsUpdatedAt",
            header: "Updated",
            cell: (p: InferenceProfile) => (p.awsUpdatedAt ? new Date(p.awsUpdatedAt).toLocaleString() : "—"),
            sortingField: "awsUpdatedAt",
          },
        ]}
        resizableColumns
        wrapLines
        stickyHeader
        empty={<Box textAlign="center">No system defined inference profiles found.</Box>}
      />

      <Modal
        visible={staleModal.visible}
        onDismiss={() => setStaleModal({ visible: false, stale: [] })}
        header="Profiles no longer available in AWS"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setStaleModal({ visible: false, stale: [] })}>Cancel</Button>
              <Button
                loading={syncing}
                onClick={async () => {
                  setStaleModal({ visible: false, stale: [] });
                  await runSync([]);
                }}
              >
                Sync without deleting
              </Button>
              <Button
                variant="primary"
                loading={syncing}
                onClick={async () => {
                  const arns = staleModal.stale.map((s) => s.arn);
                  setStaleModal({ visible: false, stale: [] });
                  await runSync(arns);
                }}
              >
                Sync and delete
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Box>
            These {staleModal.stale.length} profiles are saved in ITPMNG but were not returned by AWS for{" "}
            {selectedAccount?.Alias} in {region}.
          </Box>
          <Box>
            "Sync without deleting" will still create/update the current profiles but keep the{" "}
            {staleModal.stale.length} stale ones in ITPMNG.
          </Box>
          <Table
            variant="embedded"
            items={staleModal.stale}
            columnDefinitions={[
              { id: "name", header: "Name", cell: (s: StaleProfile) => s.inferenceProfileName },
              { id: "vendor", header: "Vendor", cell: (s: StaleProfile) => s.vendor },
              { id: "model", header: "Model", cell: (s: StaleProfile) => s.modelName ?? "—" },
              { id: "arn", header: "ARN", cell: (s: StaleProfile) => s.arn },
            ]}
            empty={<Box textAlign="center">None</Box>}
          />
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}
